package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initAppframePage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("appframe.md", FrontMatterElement.Builder { addScalar("title", "appframe"); addScalar("searchTitle", "Custom title bar for Compose Desktop — macOS, Windows 11 and Linux"); addScalar("description", "A window frame for Compose Desktop that behaves like the platform it runs on, including asking GNOME where the buttons go instead of guessing."); addScalar("library", "appframe"); addScalar("post", "/blog/ask-the-desktop"); }))
}

@Page("/appframe")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun AppframePage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A custom title bar looks like a small job until you notice that \"which side do the window buttons go on\" has three different right answers, and one of them is not knowable in advance.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-handles") }) {
        org.jetbrains.compose.web.dom.Text("What it handles")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("macOS")
            }
            org.jetbrains.compose.web.dom.Text(": traffic lights on the left, dimmed when the window loses focus, glyphs revealed on hover — and the green button goes ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("fullscreen")
            }
            org.jetbrains.compose.web.dom.Text(", not maximize, which is one of the tells that an app was ported rather than written for the platform.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Windows 11")
            }
            org.jetbrains.compose.web.dom.Text(": the actual button metrics, including the red close-button hover.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Linux")
            }
            org.jetbrains.compose.web.dom.Text(": there is no convention, so the desktop is asked directly. The GTK ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("org.gnome.desktop.wm.preferences button-layout")
            }
            org.jetbrains.compose.web.dom.Text(" setting decides both the side and ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("which buttons exist at all")
            }
            org.jetbrains.compose.web.dom.Text(" — which is why a hardcoded three-button row looks wrong on stock GNOME rather than merely misplaced. Falls back to the right-hand layout when the setting cannot be read.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Maximize that respects the window manager")
            }
            org.jetbrains.compose.web.dom.Text(" via ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("WindowPlacement.Maximized")
            }
            org.jetbrains.compose.web.dom.Text(", so docks and multi-monitor setups work; rounded corners where the platform expects them, squared off while maximized.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/releases") }
dependencies { implementation("io.github.youndie:appframe-desktop:<VERSION>") }
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No icon dependency — every glyph is drawn with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Canvas")
        }
        org.jetbrains.compose.web.dom.Text(". Material 3 colours are taken from whatever ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MaterialTheme")
        }
        org.jetbrains.compose.web.dom.Text(" it finds itself in, and it is a drop-in replacement for the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Window")
        }
        org.jetbrains.compose.web.dom.Text(" block you already have.")
    }
}
